if (!customElements.get('featured-product-card')) {
  class FeaturedProductCard extends HTMLElement {
    constructor() {
      super();

      this.isRotating = false;
      this.transitionDuration = 1000;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }

    connectedCallback() {
      this.products = Array.from(
        this.querySelectorAll('[data-featured-product-card-product]')
      );

      this.swatches = Array.from(
        this.querySelectorAll('[data-featured-product-card-swatch]')
      );

      this.links = Array.from(
        this.querySelectorAll('[data-featured-product-card-link]')
      );

      if (!this.products.length || !this.swatches.length) {
        return;
      }

      const initialFrontIndex = this.products.findIndex(
        (product) => product.dataset.slot === 'front'
      );

      this.setProductSlots(
        initialFrontIndex >= 0 ? initialFrontIndex : 0
      );

      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);

      if (window.Shopify?.designMode) {
        document.addEventListener(
          'shopify:block:select',
          this.handleBlockSelect
        );
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);

      if (window.Shopify?.designMode) {
        document.removeEventListener(
          'shopify:block:select',
          this.handleBlockSelect
        );
      }
    }

    handleClick(event) {
      const swatch = event.target.closest(
        '[data-featured-product-card-swatch]'
      );

      if (!swatch) {
        return;
      }

      this.activateProduct(swatch.dataset.blockId);
    }

    handleKeydown(event) {
      const currentSwatch = event.target.closest(
        '[data-featured-product-card-swatch]'
      );

      if (!currentSwatch) {
        return;
      }

      const currentIndex = this.swatches.indexOf(currentSwatch);
      let nextIndex;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = (currentIndex + 1) % this.swatches.length;
          break;

        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex =
            (currentIndex - 1 + this.swatches.length) %
            this.swatches.length;
          break;

        case 'Home':
          nextIndex = 0;
          break;

        case 'End':
          nextIndex = this.swatches.length - 1;
          break;

        default:
          return;
      }

      event.preventDefault();

      const nextSwatch = this.swatches[nextIndex];

      nextSwatch.focus();
      this.activateProduct(nextSwatch.dataset.blockId);
    }

    handleBlockSelect(event) {
      const product = event.target.closest(
        '[data-featured-product-card-product]'
      );

      if (!product || !this.contains(product)) {
        return;
      }

      this.activateProduct(product.dataset.blockId);
    }

    async activateProduct(blockId) {
      if (this.isRotating) {
        return;
      }

      const targetIndex = this.products.findIndex(
        (product) => product.dataset.blockId === blockId
      );

      const frontIndex = this.products.findIndex(
        (product) => product.dataset.slot === 'front'
      );

      if (targetIndex === -1 || frontIndex === -1) {
        return;
      }

      if (targetIndex === frontIndex) {
        this.updateControls(blockId);
        return;
      }

      const productCount = this.products.length;

      const forwardSteps =
        (targetIndex - frontIndex + productCount) % productCount;

      const backwardSteps =
        (frontIndex - targetIndex + productCount) % productCount;

      const reverse = backwardSteps < forwardSteps;
      const steps = reverse ? backwardSteps : forwardSteps;

      this.isRotating = true;
      this.updateControls(blockId);

      for (let step = 0; step < steps; step += 1) {
        await this.rotateStep(reverse);
      }

      this.isRotating = false;
    }

    rotateStep(reverse) {
      const currentFrontIndex = this.products.findIndex(
        (product) => product.dataset.slot === 'front'
      );

      if (currentFrontIndex === -1) {
        return Promise.resolve();
      }

      const productCount = this.products.length;

      const nextFrontIndex = reverse
        ? (currentFrontIndex - 1 + productCount) % productCount
        : (currentFrontIndex + 1) % productCount;

      this.setProductSlots(nextFrontIndex);

      return new Promise((resolve) => {
        window.setTimeout(resolve, this.transitionDuration);
      });
    }

    setProductSlots(frontIndex) {
      const productCount = this.products.length;

      const leftIndex =
        (frontIndex - 1 + productCount) % productCount;

      const rightIndex =
        (frontIndex + 1) % productCount;

      this.products.forEach((product, index) => {
        let slot = 'hidden';

        if (index === frontIndex) {
          slot = 'front';
        } else if (index === leftIndex) {
          slot = 'left';
        } else if (index === rightIndex) {
          slot = 'right';
        }

        product.dataset.slot = slot;

        product.classList.toggle(
          'featured-product-card__product--closed',
          slot !== 'front'
        );

        product.setAttribute(
          'aria-hidden',
          slot === 'front' ? 'false' : 'true'
        );
      });
    }

    updateControls(blockId) {
      this.swatches.forEach((swatch) => {
        const isActive = swatch.dataset.blockId === blockId;

        swatch.classList.toggle(
          'featured-product-card__swatch--active',
          isActive
        );

        swatch.setAttribute(
          'aria-selected',
          isActive ? 'true' : 'false'
        );

        swatch.setAttribute(
          'tabindex',
          isActive ? '0' : '-1'
        );
      });

      this.links.forEach((link) => {
        const isActive = link.dataset.blockId === blockId;

        link.classList.toggle(
          'featured-product-card__link--active',
          isActive
        );

        link.hidden = !isActive;
      });
    }
  }

  customElements.define(
    'featured-product-card',
    FeaturedProductCard
  );
}